import { useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarDays, MapPin, CreditCard, KeyRound, Image as ImageIcon, Tag } from "lucide-react";
import SeasonsManagement from "../../components/SeasonsManagement";
import LocationsManagement from "../../components/LocationsManagement";
import PaymentAccountsManagement from "../../components/PaymentAccountsManagement";
import PartnerApplicationsManagement from "../../components/PartnerApplicationsManagement";
import SiteConfigurationManagement from "../../components/SiteConfigurationManagement";
import ExpenseCategoriesManagement from "../../components/ExpenseCategoriesManagement";
import { PageHeader, Tabs } from "../../ui";
import { useAppSelector } from "../../store/store";
import {
  canManageSeasons,
  canManageLocations,
  canManagePaymentAccounts,
  canManagePartnerApplications,
  isSuperAdmin,
} from "../../utils/authorization";
import "./styles.scss";

type TabKey =
  | "seasons"
  | "locations"
  | "payment-accounts"
  | "partner-applications"
  | "site-configuration"
  | "expense-categories";

const TAB_LABELS: Record<TabKey, string> = {
  seasons: "Seasons",
  locations: "Locations",
  "payment-accounts": "Payment Accounts",
  "partner-applications": "Partner Applications",
  "site-configuration": "Site Configuration",
  "expense-categories": "Expense Categories",
};

const TAB_ICONS: Record<TabKey, ReactNode> = {
  seasons: <CalendarDays size={16} />,
  locations: <MapPin size={16} />,
  "payment-accounts": <CreditCard size={16} />,
  "partner-applications": <KeyRound size={16} />,
  "site-configuration": <ImageIcon size={16} />,
  "expense-categories": <Tag size={16} />,
};

const Configuration = () => {
  const currentUser = useAppSelector((state) => state.auth.currentUser);
  const [searchParams, setSearchParams] = useSearchParams();

  const canSeasons = canManageSeasons(currentUser);
  const canLocations = canManageLocations(currentUser);
  const canPaymentAccounts = canManagePaymentAccounts(currentUser);
  const canPartners = canManagePartnerApplications(currentUser);
  const canSiteConfiguration = isSuperAdmin(currentUser);
  const canExpenseCategories = isSuperAdmin(currentUser);

  const availableTabs = useMemo<TabKey[]>(() => {
    const tabs: TabKey[] = [];
    if (canSeasons) tabs.push("seasons");
    if (canLocations) tabs.push("locations");
    if (canPaymentAccounts) tabs.push("payment-accounts");
    if (canPartners) tabs.push("partner-applications");
    if (canSiteConfiguration) tabs.push("site-configuration");
    if (canExpenseCategories) tabs.push("expense-categories");
    return tabs;
  }, [canSeasons, canLocations, canPaymentAccounts, canPartners, canSiteConfiguration, canExpenseCategories]);

  const requestedTab = searchParams.get("tab") as TabKey | null;
  const activeTab: TabKey = requestedTab && availableTabs.includes(requestedTab)
    ? requestedTab
    : availableTabs[0];

  const handleTabChange = (tab: TabKey) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      // Reset per-tab state so search/sort/page from one tab doesn't bleed.
      next.delete("page");
      next.delete("search");
      next.delete("sortColumn");
      next.delete("sortDirection");
      return next;
    });
  };

  useEffect(() => {
    document.title = activeTab ? `Configuration · ${TAB_LABELS[activeTab]}` : "Configuration";
  }, [activeTab]);

  return (
    <div className="configuration-page">
      <PageHeader title="Configuration" subtitle="Seasons, locations, payment accounts, and third-party API keys.">
        {availableTabs.length > 1 && (
          <Tabs<TabKey>
            ariaLabel="Configuration sections"
            options={availableTabs.map((tab) => ({ value: tab, label: TAB_LABELS[tab], icon: TAB_ICONS[tab] }))}
            value={activeTab}
            onChange={handleTabChange}
          />
        )}
      </PageHeader>

      <div className="tab-panel">
        {activeTab === "seasons" && canSeasons && <SeasonsManagement canManage={canSeasons} />}
        {activeTab === "locations" && canLocations && <LocationsManagement canManage={canLocations} />}
        {activeTab === "payment-accounts" && canPaymentAccounts && <PaymentAccountsManagement canManage={canPaymentAccounts} />}
        {activeTab === "partner-applications" && canPartners && <PartnerApplicationsManagement canManage={canPartners} />}
        {activeTab === "site-configuration" && canSiteConfiguration && <SiteConfigurationManagement canManage={canSiteConfiguration} />}
        {activeTab === "expense-categories" && canExpenseCategories && <ExpenseCategoriesManagement canManage={canExpenseCategories} />}
      </div>
    </div>
  );
};

export default Configuration;
